import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ContentsBase } from '@routes/tickets/ticket-edit/contents/contents-base';
import { Basic } from '@shared/models/basic';
import { FormControl, FormGroup } from '@angular/forms';
import { Code } from '@shared/models/code';
import { debounceTime, distinctUntilChanged, finalize, takeWhile } from 'rxjs/operators';
import Utils from '@shared/utils/utils';
import { TicketCreate } from '@shared/models/ticket-create';
import { Product } from '@shared/models/product';
import { Constants } from '@shared/constants/constants';
import { Customer } from '@shared/models/customer';
import Swal from 'sweetalert2';
import { Attachment } from '@shared/models/ticket-info';
import { AttachmentViewComponent } from '@routes/dialogs/attachment-view/attachment-view.component';
import { Company } from '@shared/enums/company';
import Role from '@shared/constants/role';
import * as _ from 'lodash-es';

@Component({
  selector: 'app-basic',
  templateUrl: './basic.component.html',
  styleUrls: ['./basic.component.scss']
})
export class BasicComponent extends ContentsBase implements OnInit {

  basic: Basic = {};
  product: Product = {};
  customer: Customer = {};

  basicForm: FormGroup;
  customerTypeOptions: Code[] = [];
  dealerOptions: Code[] = [];
  storeOptions: Code[] = [];
  serviceTypeOptions: Code[] = [];

  serviceType: any;
  detailType: any;
  status: any;
  dealerPortalServiceType: any;
  processType: any;

  detailTypes: any = [];
  statusList: any = [];
  reasonList: any = [];
  processTypes: any = [];
  cloneReasonList: any = [];
  serviceTypeRestriction: any;
  showServiceTypeRestriction: boolean = false;

  dealerType: any = '';

  displayUnitRepairType: any;
  displayUnitRepairTypes: any;
  detailDescription = new FormControl(null);
  detailFile?: any;
  displayUnitRepairTypeOptions = [
    { code: 'FUNCTIONAL', name: 'Functional' },
    { code: 'COSMETIC', name: 'Cosmetic' },
    { code: 'FUNCTIONAL_COSMETIC', name: 'Both Cosmetic + Functional' },
  ];

  filename: string = '';
  files: any[] = [];
  attachFiles: any[] = [];
  attachment = null;

  companies = Company;
  isDraftTicket = false;

  showCustomerSearch = true;

  @ViewChild('imgFileInput') imgFileInput: ElementRef;
  protected readonly Company = Company;

  get form(): any {
    return this.basicForm?.controls;
  }

  get attachPreviewInitInfo() {
    return { userType: Role.dealer, lastRejectedAt: null };
  }

  ngOnInit() {
    this.init('basic');
    this.customerTypeOptions = [{
      code: this.constants.customerType.CUSTOMER,
      name: this.constants.customerType.CUSTOMER
    }, {
      code: this.constants.customerType.STORE,
      name: this.constants.customerType.STORE
    }];

    this.ticketManager.lookupChanged$
      .pipe(takeWhile(() => this.alive))
      .subscribe(load => {

        Object.keys(load).forEach(key => {
          this[key] = load[key];
        });

        const types = this.ticketService.setTypes(this.isDetail, this.serviceType, this.detailType, this.status, this.dealerPortalServiceType, this.processTypes);
        Object.keys(types).forEach(key => {
          this[key] = types[key];
        });
      });

    this.ticket.ticketChanged$
      .pipe(takeWhile(() => this.alive))
      .subscribe(ticketInfo => {
        if (!ticketInfo) {
          return;
        }
        this.ticket.ticketInfo = ticketInfo;
        this.attachFiles = this.ticket.ticketInfo.attachments.filter(f => !f.bos) || [];
      });

    this.basicForm = this.setFormControls('basicForm');
    if (!this.isDetail) {
      if ([Company.SeaIm].includes(this.company) && (this.isDealerStaff || this.isDealerManager) || ([Company.Seca].includes(this.company) && this.isStore)) {
        this.selectDealer();
      }
      this.customerTypeChange(this.constants.customerType.CUSTOMER, true);
    }

    this.ticketService.data$
      .pipe(takeWhile(() => this.alive))
      .subscribe((ticket: TicketCreate) => {
        if (Utils.isEmpty(ticket)) {
          return;
        }

        ['basic', 'product', 'attachFile'].forEach(x => {
          this[x] = ticket[x];
        });
        if (this.isDetail) {
          this.isDraftTicket = this.basic.ticketNo.substring(0, 1) === 'D';
          const processType = this.basic.ticketNo.substring(0, 1) === '5' ? Constants.processType.SR : Constants.processType.SO;
          this.statusList = this.statusList.filter(status => status.processType === processType);
          this.form.status.setValue(this.basic?.status);
          this.reasonList = this.cloneReasonList.filter(f => f.statusCode === this.basic?.status);
          this.form.reason.setValue(this.basic?.reason);
          if (processType === Constants.processType.SR) {
            this.form.status.disable();
            if (!this.isAdmin) {
              this.form.reason.disable();
            }
          }
        }

        this.form.customerType.setValue(this.basic?.customerType);


        this.form.detailDescription.setValue(this.basic?.detailDescription);
        this.form.displayUnitRepairType.setValue(this.basic?.displayUnitRepairType);
        if (this.isDetail) {
          let fields = ['customerType', 'dealer', 'dealerStore', 'serviceType', 'detailDescription', 'displayUnitRepairType'];
          if (this.isDealer) {
            fields = fields.concat(['reason', 'status']);
          }
          this.setFormControlDisable(this.form, fields);
        }
        if (this.basic?.isCancel) {
          this.basicForm.reset();
          this.customerTypeChange(this.constants.customerType.CUSTOMER, true);
          return;
        }

        if (this.basic.customerType === this.constants.customerType.CUSTOMER) {
          if (this.basic.isPsModel) {
            this.basic.serviceType = this.constants.serviceCode.PICKUP_SERVICE;
          }
        }

        this.form.serviceType.setValue(this.basic.serviceType);
        if (this.isDetail) {
          this.serviceTypeOptions = this['serviceTypes'] || this.dealerPortalServiceType;
          this.setServiceTypeDisplay(this.basic.serviceType);
          this.attachFiles = this.ticket.ticketInfo.attachments.filter(f => !f.bos) || [];
        }
      });

    switch (this.company) {
      case Company.Seca:
        break;
      case Company.SeaIm:
    }
  }

  customerTypeChange(customerType: string, init = false) {
    customerType = [Company.Seca, Company.SeaIm].includes(this.company) ? Constants.customerType.CUSTOMER : customerType;
    this.serviceTypeOptions = this.dealerPortalServiceType.filter(serviceType => serviceType.customerType === customerType);
    this.form.customerType.setValue(customerType);
    const serviceType = this.getServiceType(this.company);
    this.serviceType = serviceType;
    this.form.serviceType.setValue(serviceType);
    const detailType = this.basic.detailType || Constants.detailType.REPAIR.code;
    const basic: any = {
      customerType,
      detailType,
      serviceType,
      isPsModel: false,
    };

    if ([Company.Seca, Company.SeaIm].includes(this.company)) {
      this.form.customerType.readonly = true;
      this.form.customerType.disable();
      this.form.serviceType.disable();
    }

    if (this.basic?.dealer) {
      this.form.dealer.setValue(null);
      this.form.dealerStore.setValue(null);
    }

    if (!init) {
      this.isTabEditor();
    }


    if (!this.isDetail) {
      this.ticketService.patch({ basic }, 'Basic :: customerTypeChange');
    }
    this.basic = {
      ...this.basic,
      ...basic,
    };
    this.serviceTypeChange(serviceType);


    this.form.detailDescription.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(text => {
        this.detailDescriptionChange(text);
      });
  }

  getServiceType(company) {
    switch (company) {
      case Company.Seca:
        return Constants.serviceCode.IN_HOME;
      case Company.SeaIm:
        return Constants.serviceCode.PICKUP_SERVICE;
      default:
        return null;
    }
  }

  serviceTypeDisplayValues(serviceType) {
    let showInputField = false;
    let showFileUpload = false;
    let showDisplayUnitRepairType = false;
    let displayUnit = false;


    // this.form.serviceType.setValue(serviceType);
    this.basic.serviceType = serviceType;
    this.serviceType = serviceType;
    switch (serviceType) {
      case Constants.serviceCode.COSMETIC_REPAIR:
      case Constants.serviceCode.INSTALL_CHECKING:
        this.serviceTypeRestriction = '* A one-time cosmetic repair may be authorized if a valid proof of purchase showing purchase or delivery within 30 days is provided.';
        this.showServiceTypeRestriction = true;
        showInputField = true;
        showFileUpload = true;
        break;
      case Constants.serviceCode.STOCK_REPAIR:
        if (this.dealerType === 'Builder') {
          this.serviceTypeRestriction = '* A one-time cosmetic repair may be authorized if a valid proof of purchase showing purchase or delivery within 30 days is provided.\';\n';
        } else {
          this.serviceTypeRestriction = '* A one-time functional repair may be authorized for units manufactured within the last 18 months';
        }
        this.showServiceTypeRestriction = true;
        break;
      case Constants.serviceCode.DEMO_SERVICE:
        showDisplayUnitRepairType = true;
        displayUnit = true;
        this.serviceTypeRestriction = '';
        this.showServiceTypeRestriction = false;
        break;
      default:
        this.serviceTypeRestriction = '';
        this.showServiceTypeRestriction = false;
        showInputField = false;
        showFileUpload = [Company.Seca].includes(this.company);
        showDisplayUnitRepairType = false;
        break;
    }
    return {
      ...this.basic,
      serviceType,
      showInputField,
      showFileUpload,
      showDisplayUnitRepairType,
      displayUnit,
    };
  }

  // TODO check
  setServiceTypeDisplay(serviceType) {

    const basic: any = this.serviceTypeDisplayValues(serviceType);
    this.ticketService.update({ basic }, 'Basic :: serviceTypeChange');
  }

  serviceTypeChange(serviceType?, init = false) {
    if (serviceType === Constants.serviceCode.PICKUP_SERVICE && ![Company.SeaIm].includes(this.company)) {
      Swal.fire({
        title: 'You cannot select \'Depot Repair\'. It would be set automatically if you select \'Depot Repair\' product',
        confirmButtonText: 'OK',
        icon: 'warning',
      }).then((result) => {
        this.serviceType = Constants.serviceCode.IN_HOME;
        this.form.serviceType.setValue(Constants.serviceCode.IN_HOME);
      });
    }

    const basic: any = this.serviceTypeDisplayValues(serviceType);

    if (this.basic?.dealer) {
      this.form.dealer.setValue(null);
      this.form.dealerStore.setValue(null);
    }

    if (!init) {
      this.isTabEditor();
    }
    const customer: Customer = {};
    this.form.serviceType.setValue(serviceType);

    if (!this.isDetail) {
      this.ticketService.patch({ basic }, 'Basic :: serviceTypeChange');
      this.ticketService.patch({ customer }, 'Customer :: serviceTypeChange');
    }
  }

  statusChange(value: any, init = false) {
    if (!init) {
      this.form.reason.setValue(null);
    }
    this.reasonList = this.cloneReasonList.filter(f => f.statusCode === value);

    const status = this.form.status.value;
    const reason = this.form.reason.value;

    const basic: any = {
      ...this.basic,
    };
    basic.status = status;
    basic.reason = reason;

    if (!init) {
      this.isTabEditor();
    }

    this.ticketService.update({ basic }, 'statusChange');
  }

  reasonChange(value: any) {
    const reason = this.form.reason.value;

    const basic: any = {
      ...this.basic,
    };
    basic.reason = reason
    this.isTabEditor();
    this.ticketService.update({ basic }, 'reasonChange');
  }

  detailDescriptionChange(value: any) {
    // const detailDescription = this.form.detailDescription.value;
    const basic: any = {
      ...this.basic,
    };
    basic.detailDescription = value;
    this.isTabEditor();
    this.ticketService.update({ basic }, 'detailDescriptionChange');
  }

  displayUnitRepairTypeChange(value: any) {
    const displayUnitRepairType = this.form.displayUnitRepairType.value;

    const basic: any = {
      ...this.basic,
    };
    basic.displayUnitRepairType = displayUnitRepairType;
    this.isTabEditor();
    this.ticketService.update({ basic }, 'displayUnitRepairTypeChange');
  }

  selectDealer(code?: any) {
    if (!code) {
      if ([Company.SeaIm].includes(this.company) && (this.isDealerStaff || this.isDealerManager) || ([Company.Seca].includes(this.company) && this.isStore)) {
        code = this.dealerOptions[0].code;
      }
    }
    this.form.dealer.setValue(code);
    if ([Company.SeaIm].includes(this.company) || ([Company.Seca].includes(this.company) && this.isStore)) {
      this.storeOptions = [];
      this.form.dealerStore.setValue(null);
      this.spinnerService.start();
      this.apiService.dealer.getStores(code)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          const dealerStore = res[0];
          this.selectDealerStore(dealerStore);
        });
      if (this.isDealerManager || this.isDealerStaff) {
        this.form.dealer.disable();
      }
    } else {
      this.storeOptions = [];
      this.form.dealerStore.setValue(null);
      this.spinnerService.start();
      this.apiService.dealer.getStores(code)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          this.storeOptions = res;

          if (this.form.customerType.value === this.constants.customerType.STORE) {
            const dealer = this.form.dealer.value;
            const basic: any = {
              ...this.basic,
            };
            basic.dealer = {
              dealer
            }
            this.ticketService.patch({ basic }, 'selectDealer');
          }
        });
    }
  }

  selectDealerStore(event: any) {
    const dealer = this.form.dealer.value;
    const { code, employeeNo, bpNo, name } = event;
    const basic: any = {
      ...this.basic,
    };
    basic.dealer = {
      dealer,
      dealerStore: code,
      employeeNo,
      bpNo,
    };
    this.form.dealerStore.setValue(code);

    if ([Company.SeaIm].includes(this.company) && (this.isDealerStaff || this.isDealerManager) || ([Company.Seca].includes(this.company) && this.isStore)) {
      this.form.dealerStore.disable();
      this.storeOptions = [
        {
          code: code,
          name: name,
        }
      ]
    }
    this.isTabEditor();

    if (this.form.customerType.value === this.constants.customerType.STORE) {
      this.ticketService.patch({ basic }, 'selectDealerStore');
    } else {
      this.ticketService.update({ basic }, 'selectDealerStore');
    }
  }

  cancelTicket(ticketNo) {
    console.log('>>>>>>>>> call cancel ticket =', ticketNo);
  }

  fileSelected(event): any {
    const files = event.target.files;
    this.files = files;


    if (this.files.length > 0) {
      const overLimit = Array.from(this.files).some((f: any) => {
        return (f.size > this.configs.fileLimit.limit);
      });

      if (overLimit) {
        this.imgFileInput.nativeElement.value = '';
        const size = (this.configs.fileLimit.limit / (1024 * 1024)).toFixed(0);
        Utils.showError({ message: `The file is too large.  Please select size ${ size }MB or less.` });
        return false;
      }
    }

    const file = files[0];
    this.detailFile = file.name;

    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('fileType', 'attachment');

    (event.target as HTMLInputElement).value = '';

    this.ticketService.patch({ attachFiles: null }, 'basic :: file');
    this.spinnerService.start();
    if (!this.isDetail) {
      this.apiService.master.serverFileUpload(formData)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(event => {

          if (event.body) {
            const file = {
              _id: event.body._id,
              filename: event.body.filename,
              key: event.body.key,
              secret: event.body.secret,
              bos: event.body.bos
            };


            this.attachFiles.push(file);
            this.ticketService.patch({ attachFiles: this.attachFiles }, 'basic :: file');
          }
        }, err => {
          Utils.showError(err);
        });
    } else {

      this.apiService.ticket.updateAttachment(formData, this.basic.ticketNo)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(event => {
          console.log(event);
          if (event.body) {
            this.attachFiles = event.body.attachments.filter(f => !f.bos) || [];
          }
        }, err => {
          Utils.showError(err);
        });
    }

  }

  /*detailFileDelete() {
    const params = {
      ticketNo: this.basic.ticketNo,
      attachmentId: this.attachFiles._id,
      secret: this.attachFiles.secret,
    };

    this.spinnerService.start();
    this.apiService.ticket.deleteAttachment(params)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.attachFiles = null;
      }, err => Utils.showError(err));
  }*/

  fileDelete(attachFile) {
    this.spinnerService.start();
    this.apiService.master.serverFileDelete(attachFile)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.attachFiles = this.attachFiles.filter(f => f._id !== attachFile._id);
        this.detailFile = this.attachFiles.length ? _.last(this.attachFiles) : null;
        this.ticketService.patch({ attachFiles: this.attachFiles }, 'basic :: file null');
      }, err => Utils.showError(err));
  }

  async selectFile(isTempFile = false, attachFile?: any) {
    if (isTempFile) {
      const { _id, secret, filename } = attachFile;
      const url = this.apiService.master.imageUrl(_id, this.basic.ticketNo, secret);
      this.fileService.downloadFile(url, filename);
      return;
    }

    if (this.isImageFile(attachFile)) {
      await this.showAttachmentViewer(this.ticket.ticketInfo, attachFile);
    } else {
      this.downloadAttachment(attachFile);
    }
  }

  downloadAttachment(attachFile: any) {
    if (!attachFile?.filename) {
      return;
    }
    const attachmentForUrl = {
      ...attachFile,
      objectKey: attachFile.objectKey || attachFile._id || attachFile.key,
      bos: attachFile.bos,
    };
    let url: string;
    if (!this.isDetail && attachFile._id && attachFile.secret && this.basic?.ticketNo) {
      url = this.apiService.master.imageUrl(attachFile._id, this.basic.ticketNo, attachFile.secret);
    } else if (this.ticket?.ticketInfo?.ticketNo) {
      url = this.apiService.ticket.attachmentUrl(this.ticket.ticketInfo, attachmentForUrl, 'attachment');
    } else {
      return;
    }
    this.fileService.downloadFile(url, attachFile.filename);
  }

  isImageFile(file): boolean {
    const name: string = file?.filename;
    if (!name) {
      return false;
    }
    return (/\.(gif|jpg|jpeg|tiff|png)$/i).test(name.toLowerCase());
  }

  getPreviewUrl(attachFile: any): string | null {
    if (!attachFile || !this.isImageFile(attachFile)) {
      return null;
    }
    if (this.isDetail && this.ticket?.ticketInfo?.ticketNo) {
      const attachment = {
        ...attachFile,
        objectKey: attachFile.objectKey || attachFile._id || attachFile.key,
        bos: attachFile.bos
      };
      return this.apiService.ticket.attachmentUrl(this.ticket.ticketInfo, attachment, 'inline');
    }
    if (!this.isDetail && this.basic?.ticketNo && attachFile._id && attachFile.secret) {
      return this.apiService.master.imageUrl(attachFile._id, this.basic.ticketNo, attachFile.secret);
    }
    return null;
  }

  showAttachmentViewer(ticketInfo, attachment: Attachment) {
    const src = this.apiService.ticket.attachmentUrl(ticketInfo, attachment, 'inline');

    const initialState = {
      attachmentImage: src,
      params: {
        ticketInfo,
        attachment,
      },
    };

    this.modalService.show(AttachmentViewComponent, {
      initialState,
      class: 'modal-lg',
    });
  }
}
